import {
  ConversationPlatform,
  ExpandedConversation,
  ExpandedConversationMessage,
  MessageVisibility,
} from "@redotech/redo-model/conversation";
import {
  getCustomerDisplayName,
  getPrimaryCustomerEmail,
  getPrimaryCustomerFacebook,
  getPrimaryCustomerInstagram,
} from "@redotech/redo-model/customer";
import { Avatar } from "@redotech/redo-web/arbiter-components/avatars/avatar";
import { AvatarLabelGroup } from "@redotech/redo-web/arbiter-components/avatars/avatar-label-group";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import capitalize from "lodash/capitalize";
import { memo, ReactNode, useMemo, useState } from "react";
import * as emailMessageCss from "./email-message.module.css";
import { getUserAvatarUrl } from "./get-avatar-url";
import { SimpleEmailMessage } from "./simple-email-message";

export interface EmailMessageContainerProps {
  message: ExpandedConversationMessage;
  conversation: ExpandedConversation;
  profilePictures?: Record<string, string>;
  teamName: string;
  startCollapsed?: boolean;
  children?: ReactNode;
  showEmailContent?: boolean;
  infoRowContent?: ReactNode;
  additionalHeaderContent?: ReactNode;
  headerRightContent?: ReactNode;
  customTimestamp?: string;
  className?: string;
  id?: string;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export const EmailMessageContainer = memo(function EmailMessageContainer({
  message,
  conversation,
  profilePictures,
  teamName,
  startCollapsed = false,
  children,
  showEmailContent = true,
  infoRowContent,
  additionalHeaderContent,
  headerRightContent,
  customTimestamp,
  className,
  id,
  onCollapsedChange,
}: EmailMessageContainerProps) {
  const [collapsed, setCollapsed] = useState(startCollapsed);
  const [mouseDownLoc, setMouseDownLoc] = useState<
    { x: number; y: number } | undefined
  >({ x: 0, y: 0 });

  const handleCollapsedChange = (newCollapsed: boolean) => {
    setCollapsed(newCollapsed);
    onCollapsedChange?.(newCollapsed);
  };

  // Get sender display name
  const getSenderDisplayName = (
    conversation: ExpandedConversation,
    message: ExpandedConversationMessage,
  ): string => {
    return message.type !== "merchant"
      ? getCustomerDisplayName(message.customer) || "Customer"
      : message.user?._id === "anonymous"
        ? `${capitalize(conversation.platform)} User`
        : message.user?.name || `${teamName} support`;
  };

  const username = getSenderDisplayName(conversation, message);
  const customerEmailOnMessage = getPrimaryCustomerEmail(message.customer);

  // Get sender avatar
  const senderImage: string | null | undefined = useMemo(() => {
    const senderEmail =
      message.type === "merchant" || message.type === "system"
        ? message.user?.email
        : customerEmailOnMessage;

    if (senderEmail) {
      const avatarUrl = getUserAvatarUrl({
        email: senderEmail,
        userId: message.user?._id,
      });
      if (avatarUrl) return avatarUrl;
    }

    // Handle customer profile pictures
    if (message.type === "customer") {
      if (message.customer?._id === conversation?.customer?.customer) {
        if (conversation.customer?.image?.url) {
          return conversation.customer.image.url;
        }

        const customerInstagram = getPrimaryCustomerInstagram(
          conversation.customer,
        );
        const customerFacebook = getPrimaryCustomerFacebook(
          conversation.customer,
        );

        if (
          [
            ConversationPlatform.INSTAGRAM,
            ConversationPlatform.INSTAGRAM_COMMENTS,
          ].includes(conversation.platform) &&
          customerInstagram?.profilePic
        ) {
          return customerInstagram?.profilePic;
        } else if (
          conversation.platform === ConversationPlatform.FACEBOOK &&
          customerFacebook?.profilePic
        ) {
          return customerFacebook.profilePic;
        }
      } else if (
        profilePictures &&
        message.customer &&
        profilePictures[message.customer._id]
      ) {
        return profilePictures[message.customer._id];
      }
    }

    return null;
  }, [message, customerEmailOnMessage, profilePictures, conversation]);

  // Determine message styling variant
  const messageVariantClassname =
    message.visibility === MessageVisibility.INTERNAL
      ? emailMessageCss.internal
      : message.type === "merchant"
        ? emailMessageCss.merchant
        : emailMessageCss.normal;

  const messageVariantProps = {
    avatarGroup: {
      headerColor:
        message.visibility === MessageVisibility.INTERNAL ||
        message.type === "merchant"
          ? "var(--redo-colors-text-text-primary-900) !important"
          : "var(--redo-colors-text-text-primary-900) !important",
      subtlerColor:
        message.visibility === MessageVisibility.INTERNAL ||
        message.type === "merchant"
          ? "var(--redo-colors-text-text-tertiary-600) !important"
          : "var(--redo-colors-text-text-primary-900) !important",
    },
  };

  return (
    <Flex
      dir="column"
      {...(!collapsed ? { pb: "xl" } : {})}
      className={classNames(
        emailMessageCss.emailContainer,
        messageVariantClassname,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        { [emailMessageCss.collapsed]: collapsed },
        className,
      )}
      id={id}
    >
      <Flex
        align="center"
        className={classNames(
          emailMessageCss.emailHeader,
          messageVariantClassname,
        )}
        gap="4xl"
        justify="space-between"
        onMouseDown={(e) => {
          setMouseDownLoc({ x: e.clientX, y: e.clientY });
        }}
        onMouseUp={(e) => {
          if (
            mouseDownLoc &&
            Math.abs(e.clientX - mouseDownLoc?.x) < 5 &&
            Math.abs(e.clientY - mouseDownLoc?.y) < 5
          ) {
            handleCollapsedChange(!collapsed);
          }
        }}
        p="xl"
      >
        <AvatarLabelGroup
          avatar={() => (
            <Avatar
              alt="avatar image"
              imageUrl={senderImage || "404"}
              name={username}
            />
          )}
          infoRow={
            collapsed ? (
              <div className={emailMessageCss.infoRowBoundedContent}>
                <Text
                  className={emailMessageCss.emailShortenedSummary}
                  color={messageVariantProps.avatarGroup.subtlerColor}
                  fontSize="xs"
                  pb="xxs"
                  pt="xxs"
                >
                  {message.content.replace(/\u200B/g, " ")}
                </Text>
              </div>
            ) : (
              <>{infoRowContent}</>
            )
          }
          usernameRow={
            <Flex align="center">
              <Text
                color={messageVariantProps.avatarGroup.headerColor}
                fontSize="sm"
                fontWeight="semibold"
              >
                {username}
              </Text>
              {additionalHeaderContent}
            </Flex>
          }
        />
        {headerRightContent || (
          <Flex align="center" justify="flex-end" shrink="0">
            <Text
              color={messageVariantProps.avatarGroup.subtlerColor}
              fontSize="xs"
            >
              {customTimestamp || new Date(message.createdAt).toLocaleString()}
            </Text>
          </Flex>
        )}
      </Flex>
      {!collapsed && (
        <>
          {showEmailContent && (
            <Flex className={emailMessageCss.emailContentPaddedContainer}>
              <SimpleEmailMessage
                messageContent={{
                  type: "messageHtml",
                  messageHtml: message.content,
                }}
              />
            </Flex>
          )}
          {children}
        </>
      )}
    </Flex>
  );
});
